import { EventBus, type Unsubscribe } from '../events/event_bus';
import type { FromServerMsgV2 as FromServerMsg, ToServerMsgV2 as ToServerMsg } from '../protocol/signaling';
import type { AuthContext, SignalingConnectionState, SignalingServerConfig } from '../runtime/types';

type SessionMessageHandler = (msg: FromServerMsg) => void;

function randomId(len = 10): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < len; i++) id += chars[Math.floor(Math.random() * chars.length)];
  return id;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private stateValue: SignalingConnectionState = 'disconnected';
  private readonly stateBus = new EventBus<SignalingConnectionState>();
  private readonly sessionHandlers = new Map<string, SessionMessageHandler>();
  private authContext: AuthContext | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private readonly pendingRequests = new Map<string, Array<{
    requestId: string;
    resolve: (msg: any) => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }>>();
  readonly clientId: string;

  constructor(private readonly config: SignalingServerConfig) {
    this.clientId = randomId(10);
  }

  get state(): SignalingConnectionState {
    return this.stateValue;
  }

  onStateChange(handler: (state: SignalingConnectionState) => void): Unsubscribe {
    return this.stateBus.subscribe(handler);
  }

  async connect(auth: AuthContext): Promise<void> {
    if (!auth.accessToken) {
      throw new Error('Auth context requires accessToken before connectSignaling().');
    }
    this.authContext = auth;
    this.intentionalClose = false;
    return this.openWebSocket();
  }

  async disconnect(): Promise<void> {
    this.intentionalClose = true;
    this.clearReconnectTimer();
    // 1000 = normal closure; the reason text is forwarded as the WS close
    // frame's `reason` field so the server can distinguish a deliberate
    // SDK shutdown (no need to emit session_conflict to the next viewer)
    // from a mid-session crash.
    try { this.ws?.close(1000, 'sdk-dispose'); } catch { /* ignore */ }
    this.ws = null;
    this.setState('disconnected');
  }

  send(msg: ToServerMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  registerSession(sessionId: string, handler: SessionMessageHandler): void {
    this.sessionHandlers.set(sessionId, handler);
  }

  unregisterSession(sessionId: string): void {
    this.sessionHandlers.delete(sessionId);
  }

  request<TReq extends { type: string; request_id?: string },
          TRes extends { type: string; request_id?: string }>(
    message: TReq,
    opts: { responseType: TRes['type']; timeoutMs?: number },
  ): Promise<TRes> {
    const timeoutMs = opts.timeoutMs ?? 5000;
    const requestId = message.request_id ?? `req-${Math.random().toString(36).slice(2, 10)}`;
    const outgoing = { ...message, request_id: requestId };
    return new Promise<TRes>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.removePendingRequest(opts.responseType, requestId);
        reject(new Error(`${opts.responseType} timeout after ${timeoutMs}ms`));
      }, timeoutMs);
      const arr = this.pendingRequests.get(opts.responseType) ?? [];
      arr.push({
        requestId,
        resolve: (m) => { clearTimeout(timer); resolve(m as TRes); },
        reject:  (e) => { clearTimeout(timer); reject(e); },
        timer,
      });
      this.pendingRequests.set(opts.responseType, arr);
      try {
        this.send(outgoing as unknown as ToServerMsg);
      } catch (err) {
        clearTimeout(timer);
        this.removePendingRequest(opts.responseType, requestId);
        reject(err as Error);
      }
    });
  }

  private removePendingRequest(type: string, requestId: string): void {
    const arr = this.pendingRequests.get(type);
    if (!arr) return;
    const i = arr.findIndex((p) => p.requestId === requestId);
    if (i >= 0) arr.splice(i, 1);
    if (arr.length === 0) this.pendingRequests.delete(type);
  }

  private openWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      const base = this.config.url.replace(/\/$/, '');
      const token = this.authContext?.accessToken ?? '';
      const url = `${base}/${this.clientId}?token=${encodeURIComponent(token)}`;

      this.setState('connecting');
      const ws = new WebSocket(url);
      this.ws = ws;

      let settled = false;
      const settle = (err?: Error): void => {
        if (settled) return;
        settled = true;
        clearTimeout(connectTimer);
        if (err) reject(err); else resolve();
      };

      // 8-second timeout — guards against cases where neither onopen nor onerror
      // fires in a timely manner (e.g. silent firewall drops, DNS hang).
      const connectTimer = setTimeout(() => {
        ws.close();
        settle(new Error('Signaling WebSocket connection timed out.'));
      }, 8000);

      ws.onopen = () => {
        this.setState('connected');
        settle();
      };

      ws.onerror = () => {
        settle(new Error('Signaling WebSocket connection failed.'));
      };

      ws.onclose = () => {
        this.ws = null;
        // If the promise hasn't settled yet (e.g. onerror didn't fire), reject now.
        settle(new Error('Signaling WebSocket closed before connecting.'));
        if (this.intentionalClose) {
          this.setState('disconnected');
        } else {
          this.setState('reconnecting');
          this.scheduleReconnect();
        }
      };

      ws.onmessage = (event: MessageEvent) => {
        if (typeof event.data === 'string') this.handleIncoming(event.data);
      };
    });
  }

  private handleIncoming(raw: string): void {
    // Drop messages that arrive after the SDK has decided to disconnect — the
    // server may still flush queued frames (e.g. a session_conflict triggered
    // by an in-flight reconnect with a new clientId) but the SDK no longer
    // owns the session and surfacing them would create false-positive UI.
    if (this.intentionalClose) return;
    let msg: FromServerMsg;
    try {
      msg = JSON.parse(raw) as FromServerMsg;
    } catch {
      return;
    }
    if (msg.type === 'event_list') {
      const arr = this.pendingRequests.get('event_list');
      if (arr && arr.length > 0) {
        const incomingId = (msg as { request_id?: string }).request_id;
        let entry;
        if (incomingId) {
          const i = arr.findIndex((p) => p.requestId === incomingId);
          if (i >= 0) { entry = arr.splice(i, 1)[0]; }
        }
        if (!entry) {
          entry = arr.shift();
        }
        if (arr.length === 0) this.pendingRequests.delete('event_list');
        if (entry) entry.resolve(msg);
      }
      return;
    }
    if (msg.type === 'session_bootstrap') {
      const sessionId = msg.request_id.startsWith('req-') ? msg.request_id.slice(4) : null;
      if (sessionId) this.sessionHandlers.get(sessionId)?.(msg);
    } else if (
      msg.type === 'sdp_offer' ||
      msg.type === 'session_conflict' ||
      msg.type === 'device_offline' ||
      msg.type === 'quality_adapted_ack' ||
      msg.type === 'camera_qos' ||
      msg.type === 'metrics_push' ||
      msg.type === 'capability_report'
    ) {
      // Route to the session handler by session_id field.
      // All these message types include session_id at the top level.
      const sid = (msg as { session_id?: string }).session_id;
      if (sid) this.sessionHandlers.get(sid)?.(msg);
    }
  }

  private setState(state: SignalingConnectionState): void {
    this.stateValue = state;
    this.stateBus.emit(state);
  }

  private scheduleReconnect(delayMs = 2000): void {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      if (!this.intentionalClose) {
        // Catch the rejection so it stays handled — unhandled rejections in dev mode
        // trigger React's error overlay which blocks all mouse input on the page.
        this.openWebSocket().catch(() => { /* onclose will schedule the next retry */ });
      }
    }, delayMs);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
